'use client';

import { Button, Feature, Heading, LayoutContainer, Picture, TextPassage } from '@petsmart-ui/sparky';
import ReactPlayer from 'react-player';

import { trackFeatureBannerClick } from '@libs/analytics';
import { useDeviceType } from '@libs/common-next';

import {
    COLOR_TABLE,
    NEW_TAB,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
    TARGET_BLANK,
    TARGET_SELF,
} from '../../constants';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { getImageProps } from '../../utils/propsImagesItems';
import type { FeatureBannerProps } from './types';
import './FeatureBanner.scss';

export default function FeatureBanner({
    header,
    description,
    cta,
    media,
    textColor,
    backgroundColor,
    isH1,
}: Readonly<FeatureBannerProps>) {
    const { isMobile } = useDeviceType();

    const style = {
        backgroundColor,
        color: COLOR_TABLE[textColor],
    };

    const buttonStyle = cta?.buttonStyle?.toLowerCase();
    const variant = buttonStyle === 'primary' ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE;
    const displayHeader = getTrimmedDisplayTitle(header);
    const { title: sectionTitle, headingTagName } = getPlpModuleSectionHeading(header, isH1);

    const bannerMedia =
        'remoteDesktopImageUrl' in media ? (
            <Picture {...getImageProps(media)} />
        ) : (
            <div className="feature-banner-video-container">
                <ReactPlayer
                    data-testid="video-player"
                    url={media.remoteVideoUrl}
                    onPlay={() => {
                        trackFeatureBannerClick('video', header);
                    }}
                    controls
                    width="100%"
                    height="100%"
                    playsinline
                    loop={media.isVideoLooped}
                    playing={media.isVideoAutoplay}
                    // muted required for autoplay
                    muted={media.isVideoAutoplay}
                />
            </div>
        );

    return (
        <LayoutContainer className="feature-banner-container">
            <Feature className="feature-banner" media={bannerMedia} style={style}>
                {displayHeader ? (
                    <Heading tagName={headingTagName as 'h1' | 'h2'} size="title">
                        {sectionTitle}
                    </Heading>
                ) : (
                    <span className="sparky-c-heading sparky-c-heading--title" />
                )}
                <TextPassage>{description}</TextPassage>
                {cta && (
                    <a
                        data-testid="feature-banner-cta"
                        target={cta.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF}
                        href={cta?.link?.webUrl}
                        onClick={() => {
                            if (cta.text) {
                                trackFeatureBannerClick(cta.text, header);
                            }
                        }}
                        rel="noreferrer"
                        className="feature-banner__link"
                    >
                        <Button variant={variant} text={cta.text} fullWidth={isMobile} size="lg" />
                    </a>
                )}
            </Feature>
        </LayoutContainer>
    );
}
